import React, { useState, useEffect } from "react";
import { Link, Navigate, useParams, useNavigate } from "react-router-dom";
import { History, Mail, Trash2, Edit } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageBreadcrumb } from "@/components/layout/PageBreadcrumb.tsx";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchCustomerById, deleteCustomer, updateCustomer } from "@/lib/api";
import {
    extractCashDiscountPercent,
    extractCustomerTagValue,
} from "@/lib/orders";
import { type ClientRecord } from "@/lib/mockClients";

// ----------------------------------------------------------------------------

interface ClienteProfiloProps {
    className?: string;
}

export function ClienteProfilo(props: ClienteProfiloProps) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className } = props;

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const { id } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState<ClientRecord | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    // Edit customer modal state
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editFullName, setEditFullName] = useState("");
    const [editEmail, setEditEmail] = useState("");
    const [editPhone, setEditPhone] = useState("");
    const [editAddress, setEditAddress] = useState("");
    const [editCity, setEditCity] = useState("");
    const [editZip, setEditZip] = useState("");
    const [editProvince, setEditProvince] = useState("");
    const [editNotes, setEditNotes] = useState("");
    const [editCode, setEditCode] = useState("");
    const [editTaxNumber, setEditTaxNumber] = useState("");
    const [editCountry, setEditCountry] = useState("");
    const [editTags, setEditTags] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        let isMounted = true;

        async function loadClient(): Promise<void> {
            if (!id) return;
            setIsLoading(true);
            try {
                const found = await fetchCustomerById(id);
                if (!isMounted) return;

                setClient(found);
            } catch (err) {
                console.error("[ClienteProfilo] Error loading customer:", err);
                if (!isMounted) return;
                setClient(null);
                toast.error("Impossibile contattare il server ngrok", {
                    id: `cliente-profilo-error-${id ?? "unknown"}`,
                    description:
                        "Non riesco a caricare il profilo cliente richiesto.",
                });
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        void loadClient();

        return () => {
            isMounted = false;
        };
    }, [id]);

    useEffect(() => {
        if (client) {
            setEditFullName(client.name || "");
            setEditEmail(client.email || "");
            setEditPhone(client.phone || "");
            setEditAddress(client.addressLine1 || "");
            setEditCity(client.city || "");
            setEditZip(client.postalCode || "");
            setEditProvince(client.province || "");
            setEditNotes(client.notes || "");
            setEditCode(client.code || "");
            setEditTaxNumber(client.taxNumber || client.code || "");
            setEditCountry(client.country || "");
            setEditTags(client.tags ? client.tags.join(", ") : "");
        }
    }, [client]);

    // -------------------------------------
    // Component functions
    // -------------------------------------

    const handleUpdateCustomer = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!client) return;
        if (!editFullName.trim() || !editEmail.trim()) {
            toast.error("Nome e Email sono obbligatori");
            return;
        }

        setIsSaving(true);
        try {
            await updateCustomer(client.id, {
                fullName: editFullName.trim(),
                email: editEmail.trim(),
                phone: editPhone.trim() || undefined,
                address: editAddress.trim() || undefined,
                city: editCity.trim() || undefined,
                state: editCountry.trim() || undefined,
                zip: editZip.trim() || undefined,
                province: editProvince.trim() || undefined,
                note: editNotes.trim() || undefined,
                notes: editNotes.trim() || undefined,
                code: editCode.trim() || editTaxNumber.trim() || undefined,
                taxNumber: editTaxNumber.trim() || editCode.trim() || undefined,
                tags: editTags
                    .split(",")
                    .map((t) => t.trim())
                    .filter(Boolean),
            });

            toast.success("Cliente aggiornato con successo");
            setIsEditModalOpen(false);

            // Reload profile
            if (id) {
                setIsLoading(true);
                const updated = await fetchCustomerById(id);
                setClient(updated);
                setIsLoading(false);
            }
        } catch (err) {
            console.error("[ClienteProfilo] Update customer failed:", err);
            toast.error("Impossibile aggiornare il cliente", {
                description: "Errore durante il salvataggio sul server.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    function handleDelete() {
        if (!client) return;

        toast.warning(`Eliminare il cliente "${client.name}"?`, {
            id: `delete-client-detail-${client.id}`,
            description:
                "Questa azione eliminerà permanentemente il cliente dal database.",
            duration: 10000,
            action: {
                label: "Elimina",
                onClick: async () => {
                    try {
                        await deleteCustomer(client.id);
                        toast.success(
                            `Cliente "${client.name}" eliminato con successo`,
                        );
                        navigate("/clienti", {
                            state: { deletedClientName: client.name },
                        });
                    } catch (err) {
                        console.error(
                            "[ClienteProfilo] Failed to delete customer:",
                            err,
                        );
                        toast.error("Impossibile eliminare il cliente");
                    }
                },
            },
            cancel: {
                label: "Annulla",
                onClick: () => {},
            },
        });
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    if (isLoading) {
        return (
            <DashboardLayout className={className}>
                <div className="w-full">
                    <Card className="shadow-sm border-gray-100">
                        <CardContent className="p-8">
                            <div className="space-y-6">
                                <div className="space-y-2">
                                    <Skeleton className="h-8 w-56" />
                                    <Skeleton className="h-4 w-72 max-w-full" />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {Array.from({ length: 6 }, (_, index) => (
                                        <div
                                            key={`client-profile-skeleton-${index}`}
                                            className="space-y-2">
                                            <Skeleton className="h-3 w-24" />
                                            <Skeleton className="h-5 w-full" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </DashboardLayout>
        );
    }

    if (!client) {
        return <Navigate to="/clienti" replace />;
    }

    return (
        <DashboardLayout className={className}>
            <div className="w-full">
                <PageBreadcrumb
                    items={[
                        { label: "Home", to: "/" },
                        { label: "Clienti", to: "/clienti" },
                        { label: client.name },
                    ]}
                />

                <div className="grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-6">
                    <Card className="shadow-sm border-gray-100">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-2xl font-bold">
                                {client.name}
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <div className="text-sm text-gray-500 mb-1">
                                        Email
                                    </div>
                                    <div className="text-base font-medium text-gray-900">
                                        {client.email || "-"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500 mb-1">
                                        Telefono
                                    </div>
                                    <div className="text-base font-medium text-gray-900">
                                        {client.phone || "-"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500 mb-1">
                                        Indirizzo
                                    </div>
                                    <div className="text-base font-medium text-gray-900">
                                        {client.addressLine1 || "-"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500 mb-1">
                                        Città / Località
                                    </div>
                                    <div className="text-base font-medium text-gray-900">
                                        {client.city
                                            ? `${client.city}${client.province ? `, ${client.province}` : ""}${client.postalCode ? ` ${client.postalCode}` : ""}`
                                            : "-"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500 mb-1">
                                        P.IVA / Codice Fiscale
                                    </div>
                                    <div className="text-base font-medium text-gray-900">
                                        {client.taxNumber || client.code || "-"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-sm text-gray-500 mb-1">
                                        Nazione
                                    </div>
                                    <div className="text-base font-medium text-gray-900">
                                        {client.country || client.state || "-"}
                                    </div>
                                </div>
                                {/*client.groupName && (
                                    <div>
                                        <div className="text-sm text-gray-500 mb-1">
                                            Gruppo Cliente
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-base font-medium text-gray-900">
                                                {client.groupName}
                                            </span>
                                            {client.discountPercent !==
                                                undefined && (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                                    Sconto:{" "}
                                                    {client.discountPercent}%
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {(client.cashDiscountPercent !== undefined ||
                                    extractCashDiscountPercent(client.tags) !==
                                        undefined) && (
                                    <div>
                                        <div className="text-sm text-gray-500 mb-1">
                                            Sconto Piede
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                                Sconto piede:{" "}
                                                {client.cashDiscountPercent ??
                                                    extractCashDiscountPercent(
                                                        client.tags,
                                                    )}
                                                %
                                            </span>
                                        </div>
                                    </div>
                                ) */}
                            </div>

                            {/*client.tags && client.tags.length > 0 && (
                                <div>
                                    <div className="text-sm text-gray-500 mb-2">
                                        Tag / Etichette
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {client.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )*/}

                            <div>
                                <div className="text-sm text-gray-500 mb-2">
                                    Note
                                </div>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                    {client.notes || "Nessuna nota presente."}
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                                <Button
                                    asChild
                                    className="bg-[#0D6EFD] hover:bg-blue-700 text-white">
                                    <Link to={`/ordini?clientId=${client.id}`}>
                                        <History className="w-4 h-4" />
                                        Storico ordini
                                    </Link>
                                </Button>

                                {/* 
                <Button
                  variant="outline"
                  className="border-gray-200 text-gray-700 hover:bg-gray-50"
                  onClick={() => setIsEditModalOpen(true)}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Modifica cliente
                </Button>
                <Button
                  variant="outline"
                  className="border-gray-200 text-gray-700 hover:text-red-600"
                  onClick={handleToggleStatus}
                >
                  {client.status === "Inattivo" ? (
                    <>
                      <UserCheck className="w-4 h-4 mr-2" />
                      Attiva account
                    </>
                  ) : (
                    <>
                      <UserX className="w-4 h-4 mr-2" />
                      Disattiva account
                    </>
                  )}
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Elimina account
                </Button>
                */}
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="shadow-sm border-gray-100">
                        <CardContent className="space-y-4">
                            {client.groupName && (
                                <div>
                                    <div className="text-sm text-gray-500 mb-1">
                                        Gruppo
                                    </div>
                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-800 border border-blue-100">
                                        {client.groupName}
                                    </span>
                                </div>
                            )}
                            {extractCustomerTagValue(
                                client.tags,
                                "payment",
                            ) && (
                                <div>
                                    <div className="text-sm text-gray-500 mb-1">
                                        Pagamento
                                    </div>
                                    <div className="text-base font-medium text-gray-900">
                                        {extractCustomerTagValue(
                                            client.tags,
                                            "payment",
                                        )}
                                    </div>
                                </div>
                            )}
                            {extractCustomerTagValue(
                                client.tags,
                                "paydescription",
                            ) && (
                                <div>
                                    <div className="text-sm text-gray-500 mb-1">
                                        Descrizione pagamento
                                    </div>
                                    <div className="text-base font-medium text-gray-900">
                                        {extractCustomerTagValue(
                                            client.tags,
                                            "paydescription",
                                        )}
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
                <DialogContent className="sm:max-w-[480px] bg-white text-gray-900">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-gray-900">
                            Modifica cliente
                        </DialogTitle>
                        <DialogDescription className="text-gray-500 text-sm">
                            Modifica le informazioni del cliente B2B nel
                            sistema.
                        </DialogDescription>
                    </DialogHeader>

                    <form
                        onSubmit={handleUpdateCustomer}
                        className="space-y-4 py-2">
                        <div className="space-y-1">
                            <Label
                                htmlFor="editFullName"
                                className="text-xs font-semibold text-gray-700">
                                Nome completo / Ragione Sociale{" "}
                                <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="editFullName"
                                required
                                value={editFullName}
                                onChange={(e) =>
                                    setEditFullName(e.target.value)
                                }
                                placeholder="es. Mario Rossi S.r.l."
                                className="bg-white border-gray-200 h-9"
                            />
                        </div>

                        <div className="space-y-1">
                            <Label
                                htmlFor="editEmail"
                                className="text-xs font-semibold text-gray-700">
                                Email <span className="text-red-500">*</span>
                            </Label>
                            <Input
                                id="editEmail"
                                type="email"
                                required
                                value={editEmail}
                                onChange={(e) => setEditEmail(e.target.value)}
                                placeholder="es. info@azienda.it"
                                className="bg-white border-gray-200 h-9"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label
                                    htmlFor="editPhone"
                                    className="text-xs font-semibold text-gray-700">
                                    Telefono
                                </Label>
                                <Input
                                    id="editPhone"
                                    value={editPhone}
                                    onChange={(e) =>
                                        setEditPhone(e.target.value)
                                    }
                                    placeholder="es. +39 02 123456"
                                    className="bg-white border-gray-200 h-9"
                                />
                            </div>

                            <div className="space-y-1">
                                <Label
                                    htmlFor="editAddress"
                                    className="text-xs font-semibold text-gray-700">
                                    Indirizzo
                                </Label>
                                <Input
                                    id="editAddress"
                                    value={editAddress}
                                    onChange={(e) =>
                                        setEditAddress(e.target.value)
                                    }
                                    placeholder="es. Via Roma 10"
                                    className="bg-white border-gray-200 h-9"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label
                                    htmlFor="editCity"
                                    className="text-xs font-semibold text-gray-700">
                                    Città
                                </Label>
                                <Input
                                    id="editCity"
                                    value={editCity}
                                    onChange={(e) =>
                                        setEditCity(e.target.value)
                                    }
                                    placeholder="Milano"
                                    className="bg-white border-gray-200 h-9"
                                />
                            </div>

                            <div className="space-y-1">
                                <Label
                                    htmlFor="editProvince"
                                    className="text-xs font-semibold text-gray-700">
                                    Provincia
                                </Label>
                                <Input
                                    id="editProvince"
                                    value={editProvince}
                                    onChange={(e) =>
                                        setEditProvince(e.target.value)
                                    }
                                    placeholder="es. PD / TV"
                                    className="bg-white border-gray-200 h-9"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <Label
                                    htmlFor="editZip"
                                    className="text-xs font-semibold text-gray-700">
                                    CAP
                                </Label>
                                <Input
                                    id="editZip"
                                    value={editZip}
                                    onChange={(e) => setEditZip(e.target.value)}
                                    placeholder="es. 35016"
                                    className="bg-white border-gray-200 h-9"
                                />
                            </div>

                            <div className="space-y-1">
                                <Label
                                    htmlFor="editCountry"
                                    className="text-xs font-semibold text-gray-700">
                                    Nazione / Stato
                                </Label>
                                <Input
                                    id="editCountry"
                                    value={editCountry}
                                    onChange={(e) =>
                                        setEditCountry(e.target.value)
                                    }
                                    placeholder="es. Italia"
                                    className="bg-white border-gray-200 h-9"
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <Label
                                htmlFor="editTaxNumber"
                                className="text-xs font-semibold text-gray-700">
                                P.IVA / Codice Fiscale
                            </Label>
                            <Input
                                id="editTaxNumber"
                                value={editTaxNumber}
                                onChange={(e) =>
                                    setEditTaxNumber(e.target.value)
                                }
                                placeholder="es. 05323420280"
                                className="bg-white border-gray-200 h-9"
                            />
                        </div>

                        <div className="space-y-1">
                            <Label
                                htmlFor="editNotes"
                                className="text-xs font-semibold text-gray-700">
                                Note B2B
                            </Label>
                            <textarea
                                id="editNotes"
                                rows={3}
                                value={editNotes}
                                onChange={(e) => setEditNotes(e.target.value)}
                                placeholder="Eventuali note sul cliente..."
                                className="w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                        </div>

                        <div className="space-y-1">
                            <Label
                                htmlFor="editTags"
                                className="text-xs font-semibold text-gray-700">
                                Tag / Etichette (separati da virgola)
                            </Label>
                            <Input
                                id="editTags"
                                value={editTags}
                                onChange={(e) => setEditTags(e.target.value)}
                                placeholder="es. isagent-si, customercode-1373, payment-p300"
                                className="bg-white border-gray-200 h-9"
                            />
                        </div>

                        <DialogFooter className="pt-4 border-t border-gray-100 flex gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsEditModalOpen(false)}
                                className="h-9 border-gray-200 text-gray-700"
                                disabled={isSaving}>
                                Annulla
                            </Button>
                            <Button
                                type="submit"
                                className="h-9 bg-[#0D6EFD] hover:bg-blue-700 text-white font-medium"
                                disabled={isSaving}>
                                {isSaving
                                    ? "Salvataggio..."
                                    : "Salva modifiche"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
